import { VFC, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Grid,
  Box,
  FormControl,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  IconButton,
  Container,
} from "@mui/material";
import { ContractUserCreateFormSchema, ContractUserEditForm, ContractUserEditFormSchema } from "./api/ContractUserForm";
import { PullDownItem } from "../common/form/FormPullDown";
import { ResultDialog, ResultResponse } from "../common/dialog/ResultDialog";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TenantService } from "../tenant/api/TenantService";
import { TenantNameRequest, TenantNameResponse } from "../tenant/api/TenantForm";
import { AxiosResponse } from "axios";
import { Utility } from "../../utils/Utility";
import { OrganizationNameRequest, OrganizationNameResponse } from "../organization/OrganizationForm";
import { OrganizationService } from "../organization/api/OrganizationService";
import { ContractUserService } from "./api/ContractUserService";
import ContractUserDialogMode from "../../entity/ContractUserDialogMode";
import { useLocation } from "react-router-dom";
import Constants from "../../utils/Constants";
import { FormLabel } from "../common/form/FormLabel";
import { AlertDialog } from "../common/dialog/AlertDialog";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { IS_MFAAUTH } from "../../entity/ContractUser";

import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EditOffOutlinedIcon from '@mui/icons-material/EditOffOutlined';

type Props = {
  isAlertDialogOpen: boolean;
  mode: string;
  contractUserId: string;
  defaultDialogMode: ContractUserDialogMode;
  handleAlertCancel: () => void;
  handleAlertOk: () => void;
};

/**
 * 確認ダイアログ
 * @param param0
 * @returns
 */
const ContractUserDialog: VFC<Props> = ({
  isAlertDialogOpen,
  mode,
  contractUserId,
  defaultDialogMode,
  handleAlertCancel,
  handleAlertOk,
}) => {

  const mfaOptions = Utility.getIsMfaAuthOption();
  const CONTENT_WIDTH = 7;
  const LABEL_WIDTH = 5;

  /***************************************************************
   * State管理
   **************************************************************/

  // ダイヤログモード
  const [dialogMode, setDialogMode] = useState<ContractUserDialogMode>(
    defaultDialogMode !== null || contractUserId !== "" ? defaultDialogMode : ContractUserDialogMode.CREATE
  );

  // MDMユーザー情報
  const [contractUserEditForm, setContractUserEditForm] = useState(new ContractUserEditForm());

  // 企業選択肢
  const [tenantOptions, setTenantOptions] = useState<PullDownItem[]>([] as PullDownItem[]);

  // 組織選択肢
  const [organizationOptions, setOrganizationOptions] = useState<PullDownItem[]>([] as PullDownItem[]);

  // ステータス選択肢
  const [lockStatusOption, setLockStatusOption] = useState<PullDownItem[]>();

  // 新規作成・編集・複製確認ダイアログ
  const [isEditAlertDialogOpen, setIsEditAlertDialogOpen] = useState(false);

  // 削除確認ダイアログ
  const [isDeleteAlertDialogOpen, setIsDeleteAlertDialogOpen] = useState(false);

  // 共通ダイアログ
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // パスワード表示用
  const [showPassword, setShowPassword] = useState(false);

  // パスワード確認表示用
  const [showPasswordAgain, setShowPasswordAgain] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    handleCancel();
    handleAlertOk();
    setIsDialogOpen(false);
  };

  // バックエンドからのメッセージを設定
  const setBackendValidMessage = (response: any) => {
    let error = response;
    if (error.fullName) {
      setError("fullName", { message: error.fullName });
    }
    if (error.email) {
      setError("email", { message: error.email });
    }
    if (error.password) {
      setError("password", { message: error.password });
    }
    if (error.passwordAgain) {
      setError("passwordAgain", { message: error.passwordAgain });
    }
    if (error.lockStatus) {
      setError("lockStatus", { message: error.lockStatus });
    }
    if (error.expireDate) {
      setError("expireDate", { message: error.expireDate });
    }
    if (error.comment) {
      setError("comment", { message: error.comment });
    }
  };
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    setIsDialogOpen(true);
    setResultResponse({
      status,
      code,
      displayMessage,
      result: [result],
      errorMessages: [],
    });
  };

  // キャンセル処理
  const handleCancel = () => {
    if (dialogMode === ContractUserDialogMode.CREATE) {
      reset({
        userId: "",
        fullName: "",
        email: "",
        password: "",
        passwordAgain: "",
        lockStatus: "",
        isMfaauth: "",
        expireDate: "",
        expireDateView: null,
        comment: ""
      });
    } else {
      setDialogMode(ContractUserDialogMode.DETAIL);
    }
    
    handleAlertCancel();
  }
  
  // パスワード表示切替
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  // パスワード確認表示切替
  const handleClickShowPasswordAgain = () => setShowPasswordAgain(!showPasswordAgain);

  /***************************************************************
     * 初期表示処理
     **************************************************************/
  useEffect(() => {
    getTenantNameList();
    getOrganizationNameList();
    setLockStatusOption(Utility.getLockStatusOption());
    if ("" !== contractUserId) {
      // テナント情報取得
      findByEditId();
    }
  }, [contractUserId]);

  // 企業名一覧取得API
  const getTenantNameList = () => {
    const service = new TenantService();
    service
      .findTenantNameList(new TenantNameRequest)
      .then((response: AxiosResponse) => {
        const tenantNameList: TenantNameResponse[] = response.data.data;
        setTenantOptions(Utility.getTenantNameOption(tenantNameList));
      })
      .catch((err) => {})
      .finally(() => {});
  }

  // 組織名一覧取得API
  const getOrganizationNameList = () => {
    const service = new OrganizationService();
    service
      .findOrganizationNameList(new OrganizationNameRequest)
      .then((response: AxiosResponse) => {
        const organizationNameList: OrganizationNameResponse[] = response.data.data;
        setOrganizationOptions(Utility.getOrganizaitonNameOption(organizationNameList));
      })
      .catch((err) => {})
      .finally(() => {}); 
  }

  // テナント情報取得API
  const findByEditId = () => {
    const service = new ContractUserService();
    service
      .findByEditId(contractUserId)
      .then((response: AxiosResponse) => {
        const contractUser = response.data.result;
        setValue("userId", contractUserId);
        setValue("fullName", contractUser.fullName);
        setValue("email", contractUser.email);
        setValue("password", contractUser.password);
        setValue("passwordAgain", contractUser.password);
        setValue("expireDate", contractUser.expireDate);
        setValue("expireDateView", contractUser.expireDate === null || contractUser.expireDate === "" ? null : new Date(contractUser.expireDate));
        setValue("lockStatus", contractUser.lockStatus);
        setValue("isMfaauth", contractUser.isMfaauth);
        setValue("comment", contractUser.comment);
        setValue("createUser", contractUser.createUser);
        setValue("createDate", contractUser.createDate);
        setValue("updateUser", contractUser.updateUser);
        setValue("updateDate", contractUser.updateDate);

        setContractUserEditForm(response.data.result);

      })
      .catch((err) => {})
      .finally(() => {});
  };

  /***************************************************************
   * 新規作成・編集処理
   **************************************************************/
  const handleEditAlertOpen = () => {
    setIsEditAlertDialogOpen(true);
  };
  // 新規作成・編集確認ダイアログ(CLOSE)
  const handleEditAlertClose = () => {
    setIsEditAlertDialogOpen(false);
  };
  // 保存ボタン押下(入力チェック)
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    reset,
    control,
    formState: { errors },
  } = useForm<ContractUserEditForm>({
    resolver: yupResolver(
      dialogMode === ContractUserDialogMode.EDIT ?  ContractUserEditFormSchema : ContractUserCreateFormSchema
    ),
    reValidateMode: "onSubmit",
    defaultValues: new ContractUserEditForm(),
  });

  // テナント情報新規作成確認API or テナント情報編集確認API
  // const handleEditBtn: SubmitHandler<ContractUserEditForm> = (hookForm) => {
  //   setValue("expireDate", hookForm.expireDateView ? Utility.registDateFormat(hookForm.expireDateView.toLocaleString()) : "");
  //   const service = new ContractUserService();
  //   switch (dialogMode) {
  //     case ContractUserDialogMode.CREATE:
  //       // 新規作成
  //       service
  //         .createConfirm(hookForm)
  //         .then((response) => {
  //           if (Constants.ERR_STATUS_OK === response.data.status) {
  //             // OK
  //             setContractUserEditForm(getValues());
  //             handleEditAlertOpen();
  //           } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
  //             // 入力違反
  //             setBackendValidMessage(response.data.errorMessages);
  //           } else {
  //             // NG
  //             setIsDialogOpen(true);
  //             setResultResponse(response.data);
  //           }
  //         })
  //         .catch((err) => {
  //           setStateResultResponse(
  //             Constants.ERR_STATUS_NG,
  //             err.response.status,
  //             err.response.statusText,
  //             Constants.E_MSG_SYSTEM_ERR
  //           );
  //         })
  //         .finally(() => {
  //           // TODO 必要ならローディング
  //         });
  //       break;
  //     case ContractUserDialogMode.EDIT:
  //     // テナント情報編集確認API
  //       service
  //         .updateConfirm(hookForm)
  //         .then((response) => {
  //           if (Constants.ERR_STATUS_OK === response.data.status) {
  //             // OK
  //             setContractUserEditForm(getValues());
  //             handleEditAlertOpen();
  //           } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
  //             // 入力違反
  //             setBackendValidMessage(response.data.errorMessages);
  //           } else {
  //             // NG
  //             setIsDialogOpen(true);
  //             setResultResponse(response.data);
  //           }
  //         })
  //         .catch((err) => {
  //           setStateResultResponse(
  //             Constants.ERR_STATUS_NG,
  //             err.response.status,
  //             err.response.statusText,
  //             Constants.E_MSG_SYSTEM_ERR
  //           );
  //         })
  //         .finally(() => {
  //           // TODO 必要ならローディング
  //         });
  //       break;
  //   }
  // };
  // テナント情報新規作成API or テナント情報編集API
  // const handleEditConfirmBtn = () => {
  //   const service = new ContractUserService();

  //   handleEditAlertClose();
  //   setContractUserEditForm(getValues());

  //   switch (dialogMode) {
  //     case ContractUserDialogMode.CREATE:
  //       service
  //         .create(contractUserEditForm)
  //         .then((response) => {
  //           if (Constants.ERR_STATUS_OK === response.data.status) {
  //             // OK
  //             setIsDialogOpen(true);
  //             setResultResponse(response.data);
  //           } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
  //             // 入力違反
  //             setBackendValidMessage(response.data.errorMessages);
  //           } else {
  //             // NG
  //             setIsDialogOpen(true);
  //             setResultResponse(response.data);
  //           }
  //         })
  //         .catch((err) => {
  //           setStateResultResponse(
  //             Constants.ERR_STATUS_NG,
  //             err.response.status,
  //             err.response.statusText,
  //             Constants.E_MSG_SYSTEM_ERR
  //           );
  //         })
  //         .finally(() => {
  //           // TODO 必要ならローディング
  //         });
  //       break;
  //     case ContractUserDialogMode.EDIT:
  //       // テナント情報編集API
  //       service
  //         .update(contractUserEditForm)
  //         .then((response) => {
  //           if (Constants.ERR_STATUS_OK === response.data.status) {
  //             // OK
  //             setIsDialogOpen(true);
  //             setResultResponse(response.data);
  //           } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
  //             // 入力違反
  //             setBackendValidMessage(response.data.errorMessages);
  //           } else {
  //             // NG
  //             setIsDialogOpen(true);
  //             setResultResponse(response.data);
  //           }
  //         })
  //         .catch((err) => {
  //           setStateResultResponse(
  //             Constants.ERR_STATUS_NG,
  //             err.response.status,
  //             err.response.statusText,
  //             Constants.E_MSG_SYSTEM_ERR
  //           );
  //         })
  //         .finally(() => {
  //           // TODO 必要ならローディング
  //         });
  //   }
  // };

  /***************************************************************
   * 削除処理
   **************************************************************/
  // // 削除確認ダイアログ(OPEN)
  // const handleDeleteAlertOpen = () => {
  //   setIsDeleteAlertDialogOpen(true);
  // };
  // // 削除確認ダイアログ(CLOSE)
  // const handleDeleteAlertClose = () => {
  //   setIsDeleteAlertDialogOpen(false);
  // };
  // // 削除ボタン押下
  // const handleDeleteBtn = () => {
  //   const service = new ContractUserService();

  //   if ("" !== contractUserId) {
  //     setIsDeleteAlertDialogOpen(false);
  //     service
  //       .delete(contractUserId)
  //       .then((response) => {
  //         if ("OK" === response.data.status) {
  //           setIsDialogOpen(true);
  //           setResultResponse(response.data);
  //         } else {
  //           setIsDialogOpen(true);
  //           setResultResponse(response.data);
  //         }
  //       })
  //       .catch((err) => {
  //         setStateResultResponse(
  //           Constants.ERR_STATUS_NG,
  //           err.response.status,
  //           err.response.statusText,
  //           Constants.E_MSG_SYSTEM_ERR
  //         );
  //       })
  //       .finally(() => {
  //         // TODO 必要ならローディング
  //       });
  //   }
  // };

  /***************************************************************
   * 編集処理
   **************************************************************/
  const handleChangeEditMode = () => {
    setDialogMode(dialogMode === ContractUserDialogMode.DETAIL ? ContractUserDialogMode.EDIT : ContractUserDialogMode.DETAIL);
  }

  /***************************************************************
   * レイアウト
   **************************************************************/

  // ダイヤログ名
  const dialogName = (dialogMode === ContractUserDialogMode.CREATE) ? "ユーザー追加" :
                     (dialogMode === ContractUserDialogMode.DETAIL) ? "ユーザー詳細" :
                                                                 "ユーザー編集";

  return (
    <>
      <Dialog 
        open={isAlertDialogOpen}
        fullWidth
      >
        <DialogTitle>{dialogName}</DialogTitle>
        {/* <DialogTitle>
          <Grid container justifyContent={"space-between"}>
            <Grid item xs={false}>{dialogName}</Grid>
            
            {(dialogMode !== ContractUserDialogMode.CREATE) && <>
              <Grid item xs={false}>
                <Container>
                  <IconButton
                    onClick={handleDeleteAlertOpen}
                  >
                    <DeleteOutlinedIcon/>
                  </IconButton>
                  <IconButton
                    onClick={handleChangeEditMode}
                  >
                    {dialogMode === ContractUserDialogMode.DETAIL ? <>
                      <EditOutlinedIcon/>
                    </> : <>
                      <EditOffOutlinedIcon/>
                    </>}
                  </IconButton>
                </Container>
              </Grid>
            </>}
          </Grid>
        </DialogTitle> */}
        <DialogContent>
          <Box>
            {/* 入力フォーム */}
            <Box sx={{ p: 4 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  {/* ユーザーID */}
                  {dialogMode !== ContractUserDialogMode.CREATE &&
                    <>
                      <FormLabel xs={LABEL_WIDTH} label="ユーザーID" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(contractUserId)}
                        </Typography>
                      </Grid>
                    </>
                  }
                  {/* 氏名 */}
                  <FormLabel xs={LABEL_WIDTH} label="氏名" />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === ContractUserDialogMode.DETAIL ? <>
                        <Grid item xs={CONTENT_WIDTH}>
                          <Typography noWrap component="div" align="left">
                            {Utility.NullEmptyStringWrap(contractUserEditForm.fullName)}
                          </Typography>
                        </Grid>
                      </> : <>
                        <FormControl fullWidth>
                          <TextField
                            fullWidth
                            size="small"
                            {...register("fullName")}
                            error={"tenantName" in errors}
                            helperText={errors.fullName?.message}
                          />
                        </FormControl>
                      </>
                    }
                  </Grid>
                  {/* メールアドレス */}
                  <FormLabel xs={LABEL_WIDTH} label="メールアドレス" />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === ContractUserDialogMode.DETAIL ? <>
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(contractUserEditForm.email)}
                        </Typography>
                      </Grid>
                    </> : <>
                      <FormControl fullWidth>
                        <TextField
                          fullWidth
                          size="small"
                          {...register("email")}
                          error={"email" in errors}
                          helperText={errors.email?.message}
                        />
                      </FormControl>
                    </>}
                  </Grid>
                  {/* ロックステータス */}
                  <FormLabel xs={LABEL_WIDTH} label="ロックステータス" />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === ContractUserDialogMode.DETAIL ? <>
                    {/* {false ? <> */}
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {lockStatusOption?.filter(item => item.key === contractUserEditForm.lockStatus)[0] !== undefined ? 
                          lockStatusOption?.filter(item => item.key === contractUserEditForm.lockStatus)[0].value : ""}
                        </Typography>
                      </Grid>
                    </> : <>
                      <FormControl fullWidth>
                        <Controller
                          control={control}
                          name="lockStatus"
                          defaultValue=""
                          render={({ field }) => (
                            <>
                              <Select size="small" {...field}>
                                <MenuItem value="">選択してください</MenuItem>
                                {lockStatusOption
                                  ? lockStatusOption.map((item) => (
                                      <MenuItem key={item.key} value={item.key}>
                                        {item.value}
                                      </MenuItem>
                                    ))
                                  : []}
                              </Select>
                              {errors.lockStatus && (
                                <FormHelperText error>
                                  {errors.lockStatus?.message}
                                </FormHelperText>
                              )}
                            </>
                          )}
                        />
                      </FormControl>
                    </>}
                  </Grid>
                  {/* MFA認証実施可否 */}
                  <FormLabel xs={LABEL_WIDTH} label="MFA認証実施可否" />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === ContractUserDialogMode.DETAIL ? <>
                        <Grid item xs={CONTENT_WIDTH}>
                          <Typography noWrap component="div" align="left">
                            {IS_MFAAUTH[contractUserEditForm.isMfaauth]}
                          </Typography>
                        </Grid>
                      </> : <>
                        <FormControl fullWidth>
                          <Controller
                            control={control}
                            name="isMfaauth"
                            defaultValue=""
                            render={({ field }) => (
                              <>
                                <Select
                                  size="small"
                                  {...field}
                                  {...register("isMfaauth")}
                                  error={"isMfaauth" in errors}
                                >
                                  <MenuItem value="">選択してください</MenuItem>
                                  {mfaOptions?.map((item) => (
                                    <MenuItem key={item.key} value={item.key}>
                                      {item.value}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {errors.isMfaauth && (
                                  <FormHelperText error>
                                    {errors.isMfaauth?.message}
                                  </FormHelperText>
                                )}
                              </>
                            )}
                          />
                        </FormControl>
                      </>
                    }
                  </Grid>
                  {/* アカウント有効期限 */}
                  <FormLabel xs={LABEL_WIDTH} label="アカウント有効期限" />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === ContractUserDialogMode.DETAIL ? <>
                        <Grid item xs={CONTENT_WIDTH}>
                          <Typography noWrap component="div" align="left">
                            {Utility.NullEmptyStringWrap(Utility.dateFormat(contractUserEditForm.expireDate))}
                          </Typography>
                        </Grid>
                      </> : <>
                        <FormControl fullWidth>
                          <Controller
                            name="expireDateView"
                            control={control}
                            render={({ field }) => (
                              <LocalizationProvider
                                dateAdapter={AdapterDateFns}
                                adapterLocale={ja}
                                dateFormats={{ monthAndYear: "yyyy年 MM月" }}
                                localeText={{
                                  previousMonth: "前月を表示",
                                  nextMonth: "次月を表示",
                                }}
                              >
                                <DatePicker
                                  slotProps={{ textField: { size: 'small' } }}
                                  {...field}/>
                              </LocalizationProvider>
                            )}
                          />
                        </FormControl>
                      </>
                    }
                  </Grid>
                  
                  {/* 備考 */}
                  <FormLabel xs={LABEL_WIDTH} label="備考" />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === ContractUserDialogMode.DETAIL ? <>
                        <Grid item xs={CONTENT_WIDTH}>
                          <Typography noWrap component="div" align="left">
                            {Utility.NullEmptyStringWrap(contractUserEditForm.comment)}
                          </Typography>
                        </Grid>
                      </> : <>
                        <FormControl fullWidth>
                          <TextField
                            fullWidth
                            size="small"
                            {...register("comment")}
                          />
                        </FormControl>
                      </>
                    }
                  </Grid>
                  {dialogMode !== ContractUserDialogMode.CREATE &&
                    <>
                      {/* 作成者 */}
                      <FormLabel xs={LABEL_WIDTH} label="作成者" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(contractUserEditForm.createUser)}
                        </Typography>
                      </Grid>
                      {/* 作成日時 */}
                      <FormLabel xs={LABEL_WIDTH} label="作成日時" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(contractUserEditForm.createDate)}
                        </Typography>
                      </Grid>
                      {/* 更新者 */}
                      <FormLabel xs={LABEL_WIDTH} label="更新者" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(contractUserEditForm.updateUser)}
                        </Typography>
                      </Grid>
                      {/* 更新日時 */}
                      <FormLabel xs={LABEL_WIDTH} label="更新日時" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(contractUserEditForm.updateDate)}
                        </Typography>
                      </Grid>
                      {/* 削除日時 */}
                      <FormLabel xs={LABEL_WIDTH} label="削除日時" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(contractUserEditForm.deleteDate)}
                        </Typography>
                      </Grid>
                    </>
                  }
                </Grid>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>キャンセル</Button>
          {/* {dialogMode !== ContractUserDialogMode.DETAIL && <>
            <Button onClick={handleSubmit(handleEditBtn)}>{mode}</Button>
          </>} */}
        </DialogActions>
      </Dialog>

      {/* 新規作成・編集確認ダイアログ */}
      {/* <AlertDialog
        isAlertDialogOpen={isEditAlertDialogOpen}
        title="保存確認"
        message="保存しますか？"
        mode="保存"
        handleAlertCancel={handleEditAlertClose}
        handleAlertOk={handleEditConfirmBtn}
      /> */}

      {/* 削除確認ダイアログ */}
      {/* <AlertDialog
        isAlertDialogOpen={isDeleteAlertDialogOpen}
        title="削除確認"
        message="削除しますか？"
        mode="削除"
        handleAlertCancel={handleDeleteAlertClose}
        handleAlertOk={handleDeleteBtn}
      /> */}

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />
    </>
  );
};

export default ContractUserDialog;